import * as React from 'react';
import { connect } from 'react-redux';
import { navigate } from 'libs/router';

import { Form, FormActions } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';

import ProfileMainInformation from 'modules/influencers/components/influencer-edit-profile/modules/main-information/ProfileMainInformation';
import ProfileSocialChannels from 'modules/influencers/components/influencer-edit-profile/modules/channels/ProfileSocialChannels';
import ProfileAbout from 'modules/influencers/components/influencer-edit-profile/modules/about/ProfileAbout';
import ProfileFashionIdentity from 'modules/influencers/components/influencer-edit-profile/modules/fashion-identity/ProfileFashionIdentity';
import ProfileSelfIdentity from 'modules/influencers/components/influencer-edit-profile/modules/self-identity/ProfileSelfIdentity';
import ProfileContentSpecialization from 'modules/influencers/components/influencer-edit-profile/modules/specialization/ProfileContentSpecialization';

import { validateChannels } from 'modules/channels/reducers/channels';
import { InfluencerProfileFormWithFacebookState, saveInfluencerProfile } from 'modules/influencers-agency/reducers/influencers-agencies-reducer';
import { flushState, loginAction } from 'components/external-integration/facebook/button/redux/actions';

import { __t, applyTranslations } from 'utils/language';
import { generateTempUniqueId } from 'utils/id';
import { getExpiredAnalyticsDataForInfluencer } from 'utils/influencers';

import './influencers-agency-create-profile.scss';

applyTranslations(require('./influencers-agency-create-profile.i18n.json'));

import type {
  ChannelForm,
  ChannelsValidationResult,
  Image,
  InfluencerProfileForm
} from 'types';
import type { FacebookButtonState } from 'components/external-integration/facebook/button/types';

type OwnProps = {
  agencyId: number,
  setIsModified: (setIsModified: boolean) => void,
  onCancel: () => void
}
type StateProps = {
  profile: InfluencerProfileFormWithFacebookState,
  profileImage: Image | undefined | null,
  facebookButtonState: FacebookButtonState
}

type DispatchProps = {
  dispatchUpdateInfluencerProfile: (
    details: InfluencerProfileFormWithFacebookState,
    profileId: string
  ) => void,
  dispatchValidateChannels: (channels: Array<ChannelForm>) => Promise<ChannelsValidationResult>,
  dispatchFlushFacebookState: () => void,
  dispatchLoginFacebook: (instagramUsername: string | null) => void
}

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  profile: InfluencerProfileForm,
  hasError: boolean
};

class InfluencersAgencyCreateProfile extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      profile: {
        id: null,
        influencerGenderAdditional: null,
        influencerGenderSelfDescribed: null,
        isAgencyManaged: false,
        agencyDetails: null,
        artistName: '',
        channels: [],
        influencerFirstName: '',
        influencerLastName: '',
        influencerDateOfBirth: null,
        influencerGender: '',
        locations: [],
        pitch: '',
        profileImage: null,
        ethnicGroup: null,
        profession: null,
        specializedIn: {},
        bodyAttributes: [],
        bodyTypeHeight: null,
        bodyTypeMass: null,
        ...(props.profile ? props.profile : {})
      },
      hasError: false
    };
  }

  componentDidMount() {
    const {
      profile,
      dispatchLoginFacebook
    } = this.props;

    if (profile?.facebookButtonState?.isFBLoginActive) {
      dispatchLoginFacebook(profile.facebookButtonState.instagramUserName ?? null);
    }
  }

  componentWillUnmount() {
    const { dispatchFlushFacebookState } = this.props;

    dispatchFlushFacebookState();
  }

  handleProfileChanged = (profile: Partial<InfluencerProfileForm>) => {
    const { setIsModified } = this.props;

    this.setState(state => ({
      profile: {
        ...state.profile,
        ...profile
      }
    }), () => setIsModified(true));
  };

  handleSpecializedInChanged = (specializedInCustom: Array<string>) => {
    const { setIsModified } = this.props;

    this.setState(state => ({
      profile: {
        ...state.profile,
        specializedIn: {
          ...state.profile.specializedIn,
          custom: specializedInCustom
        }
      }
    }), () => setIsModified(true));
  };

  handleChannelsChanged = (channels: Array<ChannelForm>) => {
    const { setIsModified } = this.props;

    this.setState(state => ({
      profile: {
        ...state.profile,
        channels
      }
    }), () => setIsModified(true));
  };

  handleSubmit = () => {
    const {
      profileImage,
      facebookButtonState,
      setIsModified,
      dispatchValidateChannels,
      dispatchUpdateInfluencerProfile
    } = this.props;
    const { profile } = this.state;
    const { agencyId } = this.props;

    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    setIsModified(false);

    dispatchValidateChannels(profile.channels)
      .then(result => {
        dispatchUpdateInfluencerProfile({
          ...profile,
          channels: result.channels.map(channel => ({
            ...channel,
            isInvalid: false
          })),
          profileImage,
          facebookButtonState,
          instagramExternalId: result.instagramExternalId
        }, params.profileId || generateTempUniqueId());

        navigate.to(`/influencers-agencies/${agencyId}/settings/add-new-profiles`);
      })
      .catch(result => {
        setIsModified(true);

        this.setState({
          profile: {
            ...profile,
            channels: result.channels
          }
        });
      });
  };

  handleCancel = () => {
    const { onCancel } = this.props;

    onCancel();
  };

  handleValidationErrors = () => {
    this.setState({
      hasError: true
    });
  };

  render() {
    const { profile, hasError } = this.state;

    return (
      <section>
        <Form
          onSubmit={this.handleSubmit}
          onValidationErrors={this.handleValidationErrors}
          className="influencers-agency-create-profile__form-container"
        >
          <ProfileMainInformation
            profile={profile}
            hasError={hasError}
            onChange={this.handleProfileChanged}
            isTalentAgencyManaged
          />
          <ProfileSocialChannels
            profile={profile}
            hasError={hasError}
            expiredAnalyticsDataForInfluencer={getExpiredAnalyticsDataForInfluencer([], false)}
            onChange={this.handleProfileChanged}
            isCreationStep
          />
          <ProfileContentSpecialization
            profile={profile}
            onChange={this.handleProfileChanged}
          />
          <ProfileAbout
            profile={profile}
            onChange={this.handleProfileChanged}
          />
          <ProfileFashionIdentity
            profile={profile}
            hideNew
            onChange={this.handleProfileChanged}
          />
          <ProfileSelfIdentity
            profile={profile}
            hideNew
            onChange={this.handleProfileChanged}
          />
          <div className="influencers-agency-create-profile__action-container">
            <FormActions
              containerClass="influencers-agency-create-profile__form-container-inner-block"
              align="center"
            >
              <ActionButton
                color="tertiary"
                onClick={this.handleCancel}
              >
                {__t('influencers_agency_create_profile.cancel_btn')}
              </ActionButton>
              <ActionButton
                color="primary"
                type="submit"
              >
                {__t('influencers_agency_create_profile.save_btn')}
              </ActionButton>
            </FormActions>
          </div>
        </Form>
      </section>
    );
  }
}

const mapStateToProps = ({ influencersAgency, influencerImages, facebookButton }): StateProps => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());

  return {
    profile: params.profileId ? influencersAgency.newInfluencerProfiles[params.profileId] : {},
    profileImage: influencerImages.profileImage,
    facebookButtonState: facebookButton
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchUpdateInfluencerProfile: (details, profileId) => dispatch(saveInfluencerProfile(details, profileId)),
  dispatchValidateChannels: (channels) => dispatch(validateChannels(channels)),
  dispatchFlushFacebookState: () => dispatch(flushState()),
  dispatchLoginFacebook: (instagramUsername) => dispatch(loginAction(instagramUsername))
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencersAgencyCreateProfile);
